import { useEffect, useRef, type CSSProperties } from 'react';
import { Button } from '../../components/Button';
import { useSound } from '../../hooks/useSound';
import type { BoardProps } from '../../engine/types';
import { pathWord, thornRank, type ThornMove, type ThornState } from './logic';

export function ThornBoard({ state, status, dispatch, feedback }: BoardProps<ThornState, ThornMove>) {
  const playing = status === 'playing';
  const sound = useSound();
  const word = pathWord(state);
  const prevFound = useRef(state.found.length);

  useEffect(() => {
    if (state.found.length > prevFound.current) sound.play('reveal');
    prevFound.current = state.found.length;
  }, [state.found.length, sound]);

  const onTap = (index: number) => {
    if (!playing) return;
    sound.play('select');
    dispatch({ type: 'tap', index });
  };

  const orderOf = (i: number) => {
    const idx = state.path.indexOf(i);
    return idx >= 0 ? idx + 1 : undefined;
  };
  const rank = thornRank(state);

  return (
    <div className="cv-board">
      <div className="cv-thorn">
        <div className="cv-thorn__current" aria-live="polite">
          {word ? word.toUpperCase() : <span className="cv-faint">Trace a word</span>}
        </div>

        <div
          className="cv-thorn__grid"
          style={{ '--cols': state.size } as CSSProperties}
          role="group"
          aria-label="Letter grid"
        >
          {state.grid.map((ch, i) => {
            const order = orderOf(i);
            return (
              <button
                key={i}
                type="button"
                className="cv-thorn__cell"
                data-path={order ? 'true' : undefined}
                data-tail={order === state.path.length ? 'true' : undefined}
                onClick={() => onTap(i)}
                disabled={!playing}
                aria-label={`Letter ${ch}${order ? `, position ${order} in trace` : ''}`}
              >
                {ch.toUpperCase()}
                {order ? <span className="cv-thorn__order">{order}</span> : null}
              </button>
            );
          })}
        </div>

        <div className="cv-thorn__actions">
          <Button
            variant="primary"
            disabled={!playing || word.length < state.minLength}
            onClick={() => dispatch({ type: 'submit' })}
          >
            Submit
          </Button>
          <Button
            variant="ghost"
            disabled={!playing || state.path.length === 0}
            onClick={() => dispatch({ type: 'clear' })}
          >
            Clear
          </Button>
        </div>

        <div className="cv-thorn__meta">
          <span className="cv-eyebrow">{rank.label}</span>
          <span className="cv-faint">
            {state.found.length}/{state.target} to pass · {state.validWords.length} possible
          </span>
        </div>

        {state.found.length > 0 ? (
          <div className="cv-thorn__found" role="list" aria-label="Words found">
            {state.found.map((w) => (
              <span key={w} role="listitem" className="cv-chip">
                {w.toUpperCase()}
              </span>
            ))}
          </div>
        ) : null}

        {playing && state.found.length > 0 ? (
          <Button variant="ghost" onClick={() => dispatch({ type: 'finish' })}>
            Finish round
          </Button>
        ) : null}
      </div>

      <div className="cv-board__status" data-tone={feedback?.tone} aria-hidden="true">
        {feedback?.message ?? ''}
      </div>
      <p className="visually-hidden" aria-live="polite">
        {feedback?.message}
      </p>
    </div>
  );
}
